import type { Instrument, Timeframe } from "@trader/shared/MarketData"
import { VENUES } from "@trader/shared/MarketData"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import { type AccountSummary, type ChargeModel, formatDecimal } from "@trader/shared/Money"
import { barCloseTime } from "../chart/barClose.ts"

// Which paper account a replay from a picked bar trades on (docs/design/0007):
// a new Replay account starting at the bar, or an existing one that can move
// forward to it. Plain functions, tested without React.

/** The replay's start: the picked bar has closed (it is the last bar shown). */
export const replayStartTime = (instrument: Instrument, timeframe: Timeframe, barTime: number): number =>
  barCloseTime(VENUES[instrument.venue], timeframe, barTime)

/**
 * The bars a replay steps (docs/design/0011): one minute, so every open chart moves together and
 * shows its forming candle; the chart's own timeframe when the symbol has no 1m bars to step through.
 */
export const replayStep = (chart: Timeframe, hasMinuteBars: boolean): Timeframe => (hasMinuteBars ? "1m" : chart)

/**
 * Existing accounts that can replay from `start`: open Replay accounts in any
 * currency (another than the symbol's converts its P&L at the daily rate,
 * docs/design/0018), on the chart's timeframe, not finished, and not past the
 * start (a replay only moves forward; Advance brings it to the start).
 */
export const reusableAccounts = (
  accounts: ReadonlyArray<AccountSummary>,
  timeframe: Timeframe,
  start: number
): ReadonlyArray<AccountSummary> =>
  accounts.filter((account) =>
    account.status !== "Archived" &&
    account.clock._tag === "Replay" &&
    account.clock.timeframe === timeframe &&
    account.clock.status !== "Finished" &&
    account.clock.now <= start
  )

/** Why an existing account isn't offered, for the ones that trade this currency (shown greyed out). */
export const notReusableReason = (account: AccountSummary, timeframe: Timeframe, start: number): string | null => {
  if (account.clock._tag !== "Replay") return "a Realtime account"
  if (account.clock.timeframe !== timeframe) return `replays ${account.clock.timeframe} bars`
  if (account.clock.status === "Finished") return "its replay has finished"
  if (account.clock.now > start) return "already past this bar (a replay only moves forward)"
  return null
}

const dayLabel = (time: number): string =>
  new Intl.DateTimeFormat("en-GB", { timeZone: DISPLAY_TIME_ZONE, day: "2-digit", month: "short", year: "numeric" }).format(new Date(time * 1000))

/** "Replay RELIANCE 5m · 12 Jan 2026": a name that says what the account replays (at most 64 characters). */
export const replayAccountName = (instrument: Instrument, timeframe: Timeframe, start: number): string =>
  `Replay ${instrument.symbol} ${timeframe} · ${dayLabel(start)}`.slice(0, 64)

/** A synced replay with charges sizes "% of equity" orders at most this much, leaving the rest for the charges. */
export const SYNC_EQUITY_PERCENT = 95

/**
 * Fyers' NSE delivery charges per fill, as one percentage for the backtest: STT 0.1%,
 * stamp duty 0.015% (buys), exchange and SEBI fees and brokerage (₹20 or 0.3%) with
 * GST come to about 0.14% of a ₹1,00,000 order. A little above, so the backtest is
 * never richer than the paper account that pays the real charges.
 */
export const FYERS_DELIVERY_PERCENT = 0.15

/** The account's charges as the backtest's commission (null: no charges). */
export const commissionFor = (charges: ChargeModel): Readonly<Record<string, unknown>> | null => {
  switch (charges._tag) {
    case "Zero":
      return null
    case "FyersNseEquity":
      return { commissionType: "percent", commissionValue: FYERS_DELIVERY_PERCENT }
    case "Percent":
      // Basis points to percent; the minimum per fill isn't modelled (a little under, at small sizes).
      return { commissionType: "percent", commissionValue: Number(formatDecimal(charges.bps)) / 100 }
    case "PerOrder":
      return { commissionType: "cash_per_order", commissionValue: Number(formatDecimal(charges.amount)) }
  }
}

/**
 * The strategy's properties for a synced replay (docs/design/0007): it trades from
 * the replay's start (`tradeFrom`; earlier bars only warm its indicators up) with
 * the account's starting cash as its initial capital, so the backtest and the paper
 * account start flat at the same bar with the same money. When the account pays
 * charges, the backtest pays them too (its commission, `commissionFor`), and "% of
 * equity" orders above 95% are sized at 95%: at 100% the paper account couldn't pay
 * the charges on top of an order the backtest affords.
 */
export const syncedProperties = (
  properties: Readonly<Record<string, unknown>>,
  defaults: Readonly<{ readonly defaultQtyType?: unknown; readonly defaultQtyValue?: unknown }> | undefined,
  cash: number,
  charges: ChargeModel,
  start: number
): { readonly properties: Readonly<Record<string, unknown>>; readonly sizedDown: boolean } => {
  const commission = commissionFor(charges)
  const type = properties["defaultQtyType"] ?? defaults?.defaultQtyType
  const value = Number(properties["defaultQtyValue"] ?? defaults?.defaultQtyValue)
  const sizedDown = commission !== null && type === "percent_of_equity" && value > SYNC_EQUITY_PERCENT
  return {
    properties: {
      ...properties,
      initialCapital: cash,
      tradeFrom: start,
      ...commission,
      ...(sizedDown ? { defaultQtyValue: SYNC_EQUITY_PERCENT } : {})
    },
    sizedDown
  }
}
